import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { CopyToClipboard } from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import { apiUrl } from "../../utils/backendUrl";
import { socket } from "../../routes/IsLoggedIn";
import { useMiniChat, miniChatBus } from "../../context/MiniChatContext";
import styles from "./MiniChat.module.css";
import VoiceNotePlayer from "./VoiceNotePlayer";
import useVoiceNoteRecorder from "./useVoiceNoteRecorder";
import {
  buildMessageSharePayload,
  normalizeSharingConfig,
  shareExternally,
} from "../../utils/publicSharing";
import { queryClient } from "../../App";
import {
  getPublicConfigQueryKey as getTenantPublicConfigQueryKey,
  usePublicConfigValue,
} from "../../hooks/usePublicConfig";
import {
  buildTenantRelativePath,
  getTenantPreviewSlug,
} from "../../utils/tenantPreview";
import {
  getPlatformAvatarUrl,
  isUnifloSystemIdentity,
} from "../../utils/systemAvatar";
import { renderInlineRichText } from "../../utils/richText";
import {
  ANY_FILE_ACCEPT,
  getFileTypeMetadata,
  inferAttachmentType,
} from "../../utils/fileTypes";
import {
  formatCardReferenceMeta,
  getCardReferenceFromDataTransfer,
  hasCardReferenceDrag,
} from "../../utils/cardReferences";

const MINI_CHAT_TEXTAREA_MIN_HEIGHT = 36;
const MINI_CHAT_TEXTAREA_MAX_HEIGHT = 112;

// ─── Helpers ─────────────────────────────────────────────────────────

function friendlyTimeShort(isoDate) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Math.max(0, Date.now() - d.getTime());
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  return `${days}d`;
}

function isCallMessage(message) {
  return message?.type === "call" || Boolean(message?.call);
}

function getCallIconClass(call) {
  return call?.type === "audio" ? "fa-phone" : "fa-video";
}

function formatVoiceTimer(durationMs) {
  const totalSeconds = Math.max(0, Math.floor((Number(durationMs) || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${`${seconds}`.padStart(2, "0")}`;
}

function BondIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M8.1 9.2a2.7 2.7 0 0 1 0-3.8l1.3-1.3a2.7 2.7 0 0 1 3.8 0 2.7 2.7 0 0 1 0 3.8l-1 1"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.9 14.8a2.7 2.7 0 0 1 0 3.8l-1.3 1.3a2.7 2.7 0 0 1-3.8 0 2.7 2.7 0 0 1 0-3.8l1-1"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9.4 14.6 14.6 9.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function resolveFriendshipStatus(response) {
  return (
    response?.data?.data?.friendshipStatus ||
    response?.data?.friendshipStatus ||
    response?.friendshipStatus ||
    "none"
  );
}

// ─── Component ───────────────────────────────────────────────────────

export default function MiniChat({ chat, rightPosition }) {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { currentUser, closeMiniChat, toggleMinimize, setConversationId } =
    useMiniChat();
  const onlineUsers = useSelector(
    (state) => state.soketSlice?.onlineUsers || [],
  );

  const { id: chatId, user: friend, conversationId, minimized } = chat;
  const miniChatId = useId();
  const messageAreaId = `${miniChatId}-messages`;

  const [messages, setMessages] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(
    conversationId || "",
  );
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesTotalPages, setMessagesTotalPages] = useState(1);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [dragMode, setDragMode] = useState("files");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [copiedMessageKey, setCopiedMessageKey] = useState("");
  const [bondMenuOpen, setBondMenuOpen] = useState(false);
  const [bondStatus, setBondStatus] = useState("none");
  const [bondStatusLoading, setBondStatusLoading] = useState(false);
  const [bondActionBusy, setBondActionBusy] = useState("");
  const [callMenuOpen, setCallMenuOpen] = useState(false);
  const [callLoading, setCallLoading] = useState("");
  const publicConfig = usePublicConfigValue();
  const tenantPreviewSlug = getTenantPreviewSlug();
  const siteName =
    `${
      publicConfig?.branding?.siteName ||
      publicConfig?.general?.siteName ||
      publicConfig?.platformBranding?.siteName ||
      ""
    }`.trim() ||
    "Uniflo";
  const sharingConfig = normalizeSharingConfig(publicConfig);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const messageAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const bondMenuRef = useRef(null);
  const callMenuRef = useRef(null);
  const dragCounterRef = useRef(0);
  const initialScrollLockRef = useRef(false);
  const copyResetTimeoutRef = useRef(null);
  const preserveScrollRef = useRef(null);
  // True once this mount has observed a live socket connection; lets the
  // reconnect resync skip the socket's very first connect (open-time load covers it).
  const hadSocketConnectionRef = useRef(Boolean(socket?.connected));
  const {
    isSupported: voiceNotesSupported,
    isRecording: isVoiceNoteRecording,
    isProcessing: isVoiceNoteProcessing,
    elapsedMs: voiceNoteElapsedMs,
    previewFile: pendingVoiceNoteFile,
    previewUrl: pendingVoiceNoteUrl,
    previewDurationMs: pendingVoiceNoteDurationMs,
    startRecording: startVoiceNoteRecording,
    stopRecording: stopVoiceNoteRecording,
    cancelRecording: cancelVoiceNoteRecording,
    clearPreview: clearVoiceNotePreview,
  } = useVoiceNoteRecorder({
    onError: (message) => setError(message),
  });
  const hasPendingVoiceNote = Boolean(pendingVoiceNoteFile);
  const resolvedConversationId = activeConversationId || conversationId || "";

  const resizeInput = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    input.style.height = "0px";
    const nextHeight = Math.min(input.scrollHeight, MINI_CHAT_TEXTAREA_MAX_HEIGHT);
    input.style.height = `${Math.max(nextHeight, MINI_CHAT_TEXTAREA_MIN_HEIGHT)}px`;
    input.style.overflowY =
      input.scrollHeight > MINI_CHAT_TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
  }, []);

  const friendName =
    `${friend.displayName || friend?.custom_fields?.displayName || ""}`.trim() ||
    `${friend.first_name || friend.firstName || ""} ${friend.last_name || friend.lastName || ""}`.trim() ||
    friend.username ||
    "User";
  const friendUserId = `${friend?._id || friend?.id || ""}`.trim();
  const isFriendOnline = onlineUsers.some((onlineUser) => {
    const onlineUserId = `${onlineUser?.id || onlineUser?._id || onlineUser?.userId || ""}`.trim();
    return Boolean(friendUserId && onlineUserId === friendUserId);
  });
  const friendPresenceLabel = isFriendOnline
    ? `${friendName} is online`
    : `${friendName} is offline`;

  const isUnifloAssistant = isUnifloSystemIdentity(friend);
  // For the Uniflo AI assistant, always render the platform branding icon
  // (platformBranding.logoUrl via getPlatformAvatarUrl) rather than the
  // generic initial / default photo. All other partners keep prior behavior.
  const friendAvatarSrc = isUnifloAssistant
    ? getPlatformAvatarUrl(publicConfig)
    : `${friend?.photo || ""}`.trim() || "/images/default_pic.png";

  const refreshClanWorkspaceQueries = useCallback(() => {
    if (!isUnifloAssistant) return;

    const workspaceQueryKeys = [
      ["home-access-bootstrap", tenantPreviewSlug],
      ["workspace-shell-bootstrap", tenantPreviewSlug],
      ["workspace-members", tenantPreviewSlug],
      ["workspace-boards", tenantPreviewSlug],
      ["homeFeed", tenantPreviewSlug],
      getTenantPublicConfigQueryKey(tenantPreviewSlug),
    ];

    workspaceQueryKeys.forEach((queryKey) => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.refetchQueries({ queryKey, type: "active" });
    });
  }, [isUnifloAssistant, tenantPreviewSlug]);

  const loadBondStatus = useCallback(
    async ({ silent = false } = {}) => {
      if (!friendUserId || isUnifloAssistant) return "none";

      if (!silent) setBondStatusLoading(true);
      try {
        const response = await axios.get(
          apiUrl(`/api/v1/friends/status/${friendUserId}`),
          { withCredentials: true },
        );
        const nextStatus = resolveFriendshipStatus(response);
        setBondStatus(nextStatus);
        return nextStatus;
      } catch (error) {
        if (!silent) {
          toast.error(
            error?.response?.data?.message || "Could not load bond actions.",
          );
        }
        return "none";
      } finally {
        if (!silent) setBondStatusLoading(false);
      }
    },
    [friendUserId, isUnifloAssistant],
  );

  const bondActions = (() => {
    if (isUnifloAssistant || !friendUserId) return [];
    if (bondStatus === "self") return [];
    if (bondStatus === "pending_sent") {
      return [
        {
          id: "cancel",
          label: "Cancel Request",
          method: "delete",
          path: `/api/v1/friends/cancel/${friendUserId}`,
        },
        {
          id: "block",
          label: "Block",
          method: "post",
          path: `/api/v1/friends/block/${friendUserId}`,
          danger: true,
        },
      ];
    }
    if (bondStatus === "pending_received") {
      return [
        {
          id: "accept",
          label: "Accept Request",
          method: "post",
          path: `/api/v1/friends/accept/${friendUserId}`,
        },
        {
          id: "reject",
          label: "Reject Request",
          method: "delete",
          path: `/api/v1/friends/reject/${friendUserId}`,
        },
        {
          id: "block",
          label: "Block",
          method: "post",
          path: `/api/v1/friends/block/${friendUserId}`,
          danger: true,
        },
      ];
    }
    if (bondStatus === "accepted") {
      return [
        {
          id: "unfriend",
          label: "Unfriend",
          method: "delete",
          path: `/api/v1/friends/remove/${friendUserId}`,
        },
        {
          id: "block",
          label: "Block",
          method: "post",
          path: `/api/v1/friends/block/${friendUserId}`,
          danger: true,
        },
      ];
    }
    if (bondStatus === "blocked") {
      return [
        {
          id: "unblock",
          label: "Unblock",
          method: "delete",
          path: `/api/v1/friends/block/${friendUserId}`,
        },
      ];
    }
    if (bondStatus === "blocked_by_them") {
      return [];
    }
    return [
      {
        id: "request",
        label: "Request Friend",
        method: "post",
        path: `/api/v1/friends/request/${friendUserId}`,
      },
      {
        id: "block",
        label: "Block",
        method: "post",
        path: `/api/v1/friends/block/${friendUserId}`,
        danger: true,
      },
    ];
  })();

  const handleBondMenuToggle = useCallback(
    (event) => {
      event.stopPropagation();
      if (!friendUserId || isUnifloAssistant) return;

      setBondMenuOpen((wasOpen) => {
        const shouldOpen = !wasOpen;
        if (shouldOpen) {
          setCallMenuOpen(false);
          void loadBondStatus();
          if (minimized) {
            toggleMinimize(chatId);
          }
        }
        return shouldOpen;
      });
    },
    [
      chatId,
      friendUserId,
      isUnifloAssistant,
      loadBondStatus,
      minimized,
      toggleMinimize,
    ],
  );

  const handleBondAction = useCallback(
    async (action) => {
      if (!action || bondActionBusy) return;

      setBondActionBusy(action.id);
      try {
        if (action.method === "delete") {
          await axios.delete(apiUrl(action.path), { withCredentials: true });
        } else {
          await axios.post(apiUrl(action.path), {}, { withCredentials: true });
        }
        toast.success(`${action.label} completed.`);
        await loadBondStatus({ silent: true });
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            `Could not complete ${action.label.toLowerCase()}.`,
        );
      } finally {
        setBondActionBusy("");
      }
    },
    [bondActionBusy, loadBondStatus],
  );

  const handleCallMenuToggle = useCallback(
    (event) => {
      event.stopPropagation();
      if (!friendUserId || isUnifloAssistant) return;
      setCallMenuOpen((wasOpen) => {
        const shouldOpen = !wasOpen;
        if (shouldOpen && minimized) {
          toggleMinimize(chatId);
        }
        if (shouldOpen) {
          setBondMenuOpen(false);
        }
        return shouldOpen;
      });
    },
    [chatId, friendUserId, isUnifloAssistant, minimized, toggleMinimize],
  );

  const initiateCall = useCallback(
    async (callType) => {
      if (!friendUserId || callLoading) return;

      setCallLoading(callType);
      setCallMenuOpen(false);
      try {
        const { data } = await axios.post(
          apiUrl("/api/v1/calls/initiate"),
          { recipientId: friendUserId, type: callType },
          { withCredentials: true },
        );
        const callId = data?.data?.call?._id;
        if (!callId) {
          throw new Error("Call could not be started.");
        }
        const returnTo = `${routeLocation.pathname || ""}${routeLocation.search || ""}`;
        navigate(buildTenantRelativePath(`/call/${callId}`), {
          state: { returnTo: returnTo || buildTenantRelativePath("/") },
        });
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Could not start the call.",
        );
      } finally {
        setCallLoading("");
      }
    },
    [callLoading, friendUserId, navigate, routeLocation.pathname, routeLocation.search],
  );

  const mergeMessages = useCallback((existing, incoming) => {
    const list = Array.isArray(incoming) ? incoming : [incoming];
    const merged = [...existing];
    const seen = new Set(
      merged.map(
        (message) => message?._id || `${message?.sender}-${message?.createdAt}`,
      ),
    );

    list.forEach((message) => {
      const key = message?._id || `${message?.sender}-${message?.createdAt}`;
      if (!seen.has(key)) {
        merged.push(message);
        seen.add(key);
      }
    });

    return merged;
  }, []);

  const loadOlderMessages = useCallback(async () => {
    if (
      !resolvedConversationId ||
      loadingOlderMessages ||
      messagesPage >= messagesTotalPages
    ) {
      return;
    }

    const messageArea = messageAreaRef.current;
    if (messageArea) {
      preserveScrollRef.current = {
        scrollHeight: messageArea.scrollHeight,
        scrollTop: messageArea.scrollTop,
      };
    }

    try {
      setLoadingOlderMessages(true);
      const nextPage = messagesPage + 1;
      const { data } = await axios.get(
        apiUrl(
          `/api/v1/messages/conversations/${resolvedConversationId}/messages?page=${nextPage}&limit=50`,
        ),
        { withCredentials: true },
      );
      const payload = data?.data || {};
      const olderMessages = Array.isArray(payload.messages) ? payload.messages : [];
      setMessages((prev) => mergeMessages(olderMessages, prev));
      setMessagesPage(Number(payload.page) || nextPage);
      setMessagesTotalPages(Number(payload.totalPages) || messagesTotalPages);
    } catch (error) {
      preserveScrollRef.current = null;
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Could not load older messages.",
      );
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [
    loadingOlderMessages,
    mergeMessages,
    messagesPage,
    messagesTotalPages,
    resolvedConversationId,
  ]);

  const scrollToBottom = useCallback(() => {
    if (minimized) return;
    const el = messageAreaRef.current;
    if (!el) return;

    const run = () => {
      el.scrollTop = el.scrollHeight;
    };

    run();
    window.requestAnimationFrame(() => {
      run();
      window.setTimeout(run, 60);
      window.setTimeout(run, 200);
      window.setTimeout(run, 600);
      window.setTimeout(run, 1200);
    });
  }, [minimized]);

  const lockBottomOnOpen = useCallback(() => {
    initialScrollLockRef.current = true;
    scrollToBottom();
    window.setTimeout(() => {
      initialScrollLockRef.current = false;
    }, 1500);
  }, [scrollToBottom]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setBondMenuOpen(false);
    setBondStatus("none");
    setBondActionBusy("");
    setCallMenuOpen(false);
    setCallLoading("");
  }, [friendUserId]);

  useEffect(() => {
    if (conversationId) {
      setActiveConversationId(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    if (!bondMenuOpen) return undefined;

    const handleDocumentPointerDown = (event) => {
      if (!bondMenuRef.current?.contains(event.target)) {
        setBondMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentPointerDown);
    return () => document.removeEventListener("mousedown", handleDocumentPointerDown);
  }, [bondMenuOpen]);

  useEffect(() => {
    if (!callMenuOpen) return undefined;

    const handleDocumentPointerDown = (event) => {
      if (!callMenuRef.current?.contains(event.target)) {
        setCallMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentPointerDown);
    return () => document.removeEventListener("mousedown", handleDocumentPointerDown);
  }, [callMenuOpen]);

  const handleMessageCopied = useCallback((messageKey) => {
    setCopiedMessageKey(messageKey);
    if (copyResetTimeoutRef.current) {
      window.clearTimeout(copyResetTimeoutRef.current);
    }
    copyResetTimeoutRef.current = window.setTimeout(() => {
      setCopiedMessageKey("");
      copyResetTimeoutRef.current = null;
    }, 1800);
  }, []);

  const canExternallyShareMessage = useCallback(
    (message) => {
      if (!sharingConfig.enabled) return false;
      const attachments = Array.isArray(message?.attachments)
        ? message.attachments
        : [];
      const hasAttachments = attachments.length > 0;
      const hasText = `${message?.content || ""}`.trim().length > 0;
      if (hasAttachments && sharingConfig.allowOutgoingMedia) {
        return true;
      }
      if (hasText && sharingConfig.allowOutgoingMessages) {
        return true;
      }
      return false;
    },
    [sharingConfig],
  );

  const shareMessageToExternalApp = useCallback(
    async (message) => {
      const attachments = Array.isArray(message?.attachments)
        ? message.attachments
        : [];
      const hasAttachments = attachments.length > 0;
      const hasText = `${message?.content || ""}`.trim().length > 0;
      const includeAttachments =
        hasAttachments && sharingConfig.allowOutgoingMedia;

      if (!sharingConfig.enabled) {
        toast.error("External sharing is disabled by the administrator.");
        return;
      }

      if (hasAttachments && !includeAttachments && !hasText) {
        toast.error("External media sharing is disabled by the administrator.");
        return;
      }

      if (!hasAttachments && !sharingConfig.allowOutgoingMessages) {
        toast.error(
          "External message sharing is disabled by the administrator.",
        );
        return;
      }

        try {
          const result = await shareExternally(
            buildMessageSharePayload({
              message,
              conversationId: resolvedConversationId,
              conversationLabel: friendName,
              siteName,
              includeAttachments,
            }),
          );
        if (result === "copied") {
          toast.success("Message copied for external sharing.");
        }
      } catch (error) {
        toast.error(error?.message || "Failed to share message.");
      }
    },
    [friendName, resolvedConversationId, sharingConfig, siteName],
  );

  // ─── File management helpers ──────────────────────────────────────

  const addFiles = useCallback((files) => {
    const fileArray = Array.from(files || []).filter(Boolean);
    const availableSlots = Math.max(0, 5 - selectedFiles.length);
    const acceptedFiles = fileArray.slice(0, availableSlots);
    if (acceptedFiles.length === 0) return;
    clearVoiceNotePreview();
    setSelectedFiles((prev) => [...prev, ...acceptedFiles].slice(0, 5));

    acceptedFiles.forEach((file) => {
      const metadata = getFileTypeMetadata({
        name: file.name,
        contentType: file.type,
        type: file.type,
      });
      const previewType = inferAttachmentType({
        ...metadata,
        name: file.name,
        contentType: file.type,
        type: file.type,
      });
      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setFilePreviews((prev) => [
          ...prev,
          { name: file.name, url, type: previewType, size: file.size, metadata },
        ]);
      } else {
        setFilePreviews((prev) => [
          ...prev,
          { name: file.name, url: null, type: previewType, size: file.size, metadata },
        ]);
      }
    });
  }, [clearVoiceNotePreview, selectedFiles.length]);

  const removeFile = useCallback((index) => {
    setSelectedFiles((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
    setFilePreviews((prev) => {
      const updated = [...prev];
      if (updated[index]?.url) URL.revokeObjectURL(updated[index].url);
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  const clearFiles = useCallback(() => {
    filePreviews.forEach((p) => {
      if (p.url) URL.revokeObjectURL(p.url);
    });
    setSelectedFiles([]);
    setFilePreviews([]);
  }, [filePreviews]);

  // ─── 1. Resolve conversation + load messages ─────────────────────

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        setLoading(true);
        setError("");

        const { data: convData } = await axios.post(
          apiUrl("/api/v1/messages/conversations"),
          { participantId: friend._id || friend.id },
          { withCredentials: true },
        );
        const conv = convData?.data?.conversation;
        if (!conv?._id) throw new Error("Could not create conversation");
        if (!mounted) return;

        setConversationId(chatId, conv._id);
        setActiveConversationId(conv._id);
        setMessagesPage(1);
        setMessagesTotalPages(1);

        const { data: msgData } = await axios.get(
          apiUrl(
            `/api/v1/messages/conversations/${conv._id}/messages?page=1&limit=50`,
          ),
          { withCredentials: true },
        );
        if (!mounted) return;
        const msgPayload = msgData?.data || {};
        const msgs = msgPayload.messages || [];
        const queuedMessages = miniChatBus.drainQueuedMessages(chatId);
        setMessages(mergeMessages(msgs, queuedMessages));
        setMessagesPage(Number(msgPayload.page) || 1);
        setMessagesTotalPages(Number(msgPayload.totalPages) || 1);
        if (queuedMessages.length > 0) {
          refreshClanWorkspaceQueries();
        }
        lockBottomOnOpen();

        try {
          await axios.patch(
            apiUrl(`/api/v1/messages/conversations/${conv._id}/read`),
            {},
            { withCredentials: true },
          );
        } catch (_) {}
      } catch (e) {
        if (mounted)
          setError(
            e?.response?.data?.message || e?.message || "Failed to load chat",
          );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();
    return () => {
      mounted = false;
    };
  }, [
    chatId,
    friend.id,
    friend._id,
    setConversationId,
    mergeMessages,
    lockBottomOnOpen,
    refreshClanWorkspaceQueries,
  ]);

  // ─── 2. Auto-scroll on new messages ──────────────────────────────

  useEffect(() => {
    if (loading || minimized) return;

    const preserve = preserveScrollRef.current;
    if (preserve && messageAreaRef.current) {
      window.requestAnimationFrame(() => {
        const messageArea = messageAreaRef.current;
        if (!messageArea) return;
        const delta = messageArea.scrollHeight - preserve.scrollHeight;
        messageArea.scrollTop = preserve.scrollTop + delta;
        preserveScrollRef.current = null;
      });
      return;
    }

    scrollToBottom();
  }, [messages, minimized, loading, scrollToBottom]);

  useEffect(() => {
    resizeInput();
  }, [draft, resizeInput]);

  // ─── 3. Focus input when opened / un-minimized ───────────────────

  useEffect(() => {
    if (!minimized && !loading) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [minimized, loading]);

  // ─── 4. Subscribe to incoming messages via miniChatBus ───────────

  useEffect(() => {
    const handler = (newMessage) => {
      const senderId = String(
        newMessage?.sender?._id || newMessage?.sender?.id || newMessage?.sender,
      );
      const friendId = String(friend._id || friend.id);

      if (senderId === friendId) {
        setMessages((prev) => mergeMessages(prev, newMessage));
        refreshClanWorkspaceQueries();
        lockBottomOnOpen();

        if (resolvedConversationId) {
          axios
            .patch(
              apiUrl(`/api/v1/messages/conversations/${resolvedConversationId}/read`),
              {},
              { withCredentials: true },
            )
            .catch(() => {});
        }
      }
    };

    miniChatBus.subscribe(chatId, handler);
    return () => miniChatBus.unsubscribe(chatId);
  }, [
    chatId,
    friend.id,
    friend._id,
    mergeMessages,
    lockBottomOnOpen,
    refreshClanWorkspaceQueries,
    resolvedConversationId,
  ]);

  // ─── 5. Listen for message status updates ────────────────────────

  useEffect(() => {
    if (!socket) return;

    const handleRead = (data) => {
      if (!data?.messageIds) return;
      const ids = new Set(data.messageIds);
      setMessages((prev) =>
        prev.map((m) => (ids.has(m._id) ? { ...m, status: "read" } : m)),
      );
    };

    const handleDelivered = (data) => {
      if (!data?.messageId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m._id === data.messageId && m.status === "sent"
            ? { ...m, status: "delivered" }
            : m,
        ),
      );
    };

    socket.on("messages_read", handleRead);
    socket.on("message_delivered", handleDelivered);
    return () => {
      socket.off("messages_read", handleRead);
      socket.off("message_delivered", handleDelivered);
    };
  }, []);

  // ─── 5b. Incoming realtime messages ──────────────────────────────
  // The server relays new DMs as "message_recieved" (legacy spelling — the
  // full Messages page already listens for it). Without this subscription
  // the workspace mini chat only ever showed messages fetched at open time,
  // so anything sent while the pane sat open never appeared until reopen.
  useEffect(() => {
    if (!socket) return;

    const handleIncoming = (newMessage) => {
      const incomingConversationId = `${newMessage?.conversation || ""}`;
      if (!incomingConversationId) return;
      if (
        resolvedConversationId &&
        incomingConversationId === `${resolvedConversationId}`
      ) {
        setMessages((prev) => mergeMessages(prev, [newMessage]));
        scrollToBottom();
      }
      // Keep the surrounding Conversations pane fresh either way.
      refreshClanWorkspaceQueries();
    };

    socket.on("message_recieved", handleIncoming);
    return () => {
      socket.off("message_recieved", handleIncoming);
    };
  }, [
    resolvedConversationId,
    mergeMessages,
    scrollToBottom,
    refreshClanWorkspaceQueries,
  ]);

  // ─── 5c. Resync after reconnect / tab return / poll ──────────────
  // Deploy restarts sever sockets many times a day; messages sent in that
  // window persist server-side but never push to this client, so we refetch
  // the latest page and merge (deduped) to close the silent-delivery hole.
  const resyncConversation = useCallback(
    async ({ markRead = false } = {}) => {
      if (!resolvedConversationId) return;
      try {
        const { data } = await axios.get(
          apiUrl(
            `/api/v1/messages/conversations/${resolvedConversationId}/messages?page=1&limit=50&markRead=${markRead ? 'true' : 'false'}`,
          ),
          { withCredentials: true },
        );
        const latest = Array.isArray(data?.data?.messages)
          ? data.data.messages
          : [];
        if (latest.length) {
          setMessages((prev) => {
            const merged = mergeMessages(prev, latest);
            return merged.length === prev.length ? prev : merged;
          });
        }
        if (markRead && !minimized && document.visibilityState === "visible") {
          axios
            .patch(
              apiUrl(
                `/api/v1/messages/conversations/${resolvedConversationId}/read`,
              ),
              {},
              { withCredentials: true },
            )
            .catch(() => {});
        }
      } catch (_) {
        // Silent background safety net; interactive loads surface their own errors.
      }
    },
    [mergeMessages, minimized, resolvedConversationId],
  );

  // Socket reconnect: socket.io fires "connect" on every reconnection.
  useEffect(() => {
    if (!socket) return;

    const handleReconnect = () => {
      if (!hadSocketConnectionRef.current) {
        hadSocketConnectionRef.current = true;
        return;
      }
      resyncConversation({ markRead: true });
      // Keep the surrounding Conversations pane fresh after the gap.
      refreshClanWorkspaceQueries();
    };

    socket.on("connect", handleReconnect);
    return () => {
      socket.off("connect", handleReconnect);
    };
  }, [refreshClanWorkspaceQueries, resyncConversation]);

  // Tab became visible again: cheap insurance for any missed window.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      resyncConversation({ markRead: true });
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [resyncConversation]);

  // Polling safety net: silent refetch of the open conversation while visible.
  useEffect(() => {
    if (!resolvedConversationId) return undefined;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      resyncConversation();
    }, 45000);
    return () => window.clearInterval(intervalId);
  }, [resolvedConversationId, resyncConversation]);

  // ─── 6. Send message (text and/or files) ──────────────────────────

  const handleSend = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      const content = draft.trim();
      const hasFiles = selectedFiles.length > 0;
      const hasVoiceNote = Boolean(pendingVoiceNoteFile);
      if (!content && !hasFiles && !hasVoiceNote) {
        return;
      }
      // Never swallow a send attempt silently — a blocked Enter press with no
      // feedback reads as "messages aren't going" (a hung in-flight request
      // during a deploy restart used to pin `sending` forever with no hint).
      if (!resolvedConversationId) {
        setError("Chat is still connecting — try again in a moment.");
        setTimeout(() => setError(""), 3000);
        return;
      }
      if (sending) {
        setError("Still sending your previous message…");
        setTimeout(() => setError(""), 3000);
        return;
      }

      try {
        setSending(true);
        setUploadProgress(0);

        let data;

        if (hasVoiceNote) {
          const formData = new FormData();
          formData.append("attachments", pendingVoiceNoteFile);
          formData.append("messageType", "voice_note");
          formData.append(
            "voiceNoteDurationMs",
            `${Math.max(0, Number(pendingVoiceNoteDurationMs) || 0)}`,
          );

          const resp = await axios.post(
            apiUrl(`/api/v1/messages/conversations/${resolvedConversationId}/messages`),
            formData,
            {
              withCredentials: true,
              timeout: 120000,
              headers: { "Content-Type": "multipart/form-data" },
            },
          );
          data = resp.data;
        } else if (hasFiles) {
          const formData = new FormData();
          if (content) formData.append("content", content);
          selectedFiles.forEach((file) => formData.append("attachments", file));

          const resp = await axios.post(
            apiUrl(`/api/v1/messages/conversations/${resolvedConversationId}/messages`),
            formData,
            {
              withCredentials: true,
              timeout: 120000,
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (evt) => {
                const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
                setUploadProgress(pct);
              },
            },
          );
          data = resp.data;
        } else {
          const resp = await axios.post(
            apiUrl(`/api/v1/messages/conversations/${resolvedConversationId}/messages`),
            { content },
            { withCredentials: true, timeout: 30000 },
          );
          data = resp.data;
        }

        const msg = data?.data?.message;
        if (msg) {
          setMessages((prev) => mergeMessages(prev, msg));
          lockBottomOnOpen();

          if (socket?.connected) {
            socket.emit("new_message", {
              message: msg,
              chat: {
                users: [
                  { _id: currentUser._id || currentUser.id },
                  { _id: friend._id || friend.id },
                ],
              },
            });
          }
        }
        setDraft("");
        clearFiles();
        clearVoiceNotePreview();
      } catch (e2) {
        setError(e2?.response?.data?.message || "Failed to send");
        setTimeout(() => setError(""), 3000);
      } finally {
        setSending(false);
        setUploadProgress(0);
      }
    },
    [
      draft,
      selectedFiles,
      sending,
      currentUser,
      friend,
      clearFiles,
      clearVoiceNotePreview,
      mergeMessages,
      lockBottomOnOpen,
      pendingVoiceNoteDurationMs,
      pendingVoiceNoteFile,
      resolvedConversationId,
    ],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleOpenCardReference = useCallback((cardRef) => {
    const cardId = `${cardRef?.cardId || ""}`.trim();
    if (!cardId || typeof window === "undefined") return;

    const openEvent = new CustomEvent("uniflo:open-workspace-card", {
      cancelable: true,
      detail: {
        cardId,
        boardId: cardRef?.boardId || "",
        boardSlug: cardRef?.boardSlug || "",
      },
    });
    const wasHandled = !window.dispatchEvent(openEvent);
    if (wasHandled) return;

    const boardSlug = `${cardRef?.boardSlug || ""}`.trim();
    if (!boardSlug) return;
    const tenantPath = tenantPreviewSlug ? `/${tenantPreviewSlug}` : "";
    window.location.assign(
      `${tenantPath}/boards/${encodeURIComponent(boardSlug)}?card=${encodeURIComponent(cardId)}`
    );
  }, [tenantPreviewSlug]);

  const sendCardReference = useCallback(
    async (cardReference) => {
      if (!cardReference?.cardId || !resolvedConversationId || sending) return;
      if (selectedFiles.length > 0 || hasPendingVoiceNote) {
        setError("Send or remove the current attachment before sharing a card.");
        setTimeout(() => setError(""), 3000);
        return;
      }

      try {
        setSending(true);
        const content = draft.trim();
        const resp = await axios.post(
          apiUrl(`/api/v1/messages/conversations/${resolvedConversationId}/messages`),
          {
            ...(content ? { content } : {}),
            cardReferences: [cardReference],
          },
          { withCredentials: true },
        );
        const msg = resp.data?.data?.message;
        if (msg) {
          setMessages((prev) => mergeMessages(prev, msg));
          lockBottomOnOpen();

          if (socket?.connected) {
            socket.emit("new_message", {
              message: msg,
              chat: {
                users: [
                  { _id: currentUser._id || currentUser.id },
                  { _id: friend._id || friend.id },
                ],
              },
            });
          }
        }
        setDraft("");
        toast.success(`${cardReference.type === "stack" ? "Stack" : "Card"} shared.`);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to share card");
        setTimeout(() => setError(""), 3000);
      } finally {
        setSending(false);
      }
    },
    [
      currentUser,
      draft,
      friend,
      hasPendingVoiceNote,
      lockBottomOnOpen,
      mergeMessages,
      resolvedConversationId,
      selectedFiles.length,
      sending,
    ],
  );

  const handleAttachmentLayout = useCallback(() => {
    if (initialScrollLockRef.current) {
      scrollToBottom();
    }
  }, [scrollToBottom]);

  // ─── 7. Clipboard paste handler ──────────────────────────────────

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file") {
          const file = items[i].getAsFile();
          if (file) files.push(file);
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        addFiles(files);
      }
    },
    [addFiles],
  );

  // ─── 8. Drag and drop handlers ───────────────────────────────────

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) {
      setDragMode(hasCardReferenceDrag(e.dataTransfer) ? "card" : "files");
      setDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setDragging(false);
      setDragMode("files");
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasCardReferenceDrag(e.dataTransfer)) {
      setDragMode("card");
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setDragging(false);
      setDragMode("files");

      const cardReference = getCardReferenceFromDataTransfer(e.dataTransfer);
      if (cardReference) {
        sendCardReference(cardReference);
        return;
      }
      const files = e.dataTransfer?.files;
      if (files?.length > 0) addFiles(files);
    },
    [addFiles, sendCardReference],
  );

  // ─── 9. File picker handler ──────────────────────────────────────

  const handleFileSelect = useCallback(
    (e) => {
      const files = e.target?.files;
      if (files?.length > 0) addFiles(files);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [addFiles],
  );

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <div
      className={`${styles.miniChat} ${minimized ? styles.minimized : ""}`}
      style={{ right: rightPosition }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      role="region"
      aria-label={`Mini chat with ${friendName}`}
      data-testid="mini-chat"
      data-chat-id={chatId}
    >
      {/* ── Header ───────────────────────────────────────────────── */}
      <div
        className={styles.header}
        data-testid="mini-chat-header"
      >
        <button
          type="button"
          className={styles.headerMainButton}
          onClick={() => toggleMinimize(chatId)}
          aria-expanded={!minimized}
          aria-controls={messageAreaId}
          aria-label={`${minimized ? "Expand" : "Minimize"} chat with ${friendName}`}
          data-testid="mini-chat-header-toggle"
        >
          <img
            src={friendAvatarSrc}
            alt=""
            className={styles.headerAvatar}
          />
          <span className={styles.headerIdentity}>
            <span className={styles.headerName}>{friendName}</span>
            <span
              className={`${styles.onlineDot} ${isFriendOnline ? styles.onlineDotActive : ""}`.trim()}
              aria-label={friendPresenceLabel}
              title={friendPresenceLabel}
              data-testid="mini-chat-online-indicator"
            />
          </span>
        </button>
        <span className={styles.headerActions}>
          {!isUnifloAssistant && friendUserId ? (
            <span className={styles.headerMenuWrap} ref={callMenuRef}>
              <button
                type="button"
                className={styles.headerBtn}
                onClick={handleCallMenuToggle}
                title={`Call ${friendName}`}
                aria-label={`Call ${friendName}`}
                aria-haspopup="menu"
                aria-expanded={callMenuOpen}
                disabled={Boolean(callLoading)}
                data-testid="mini-chat-call-actions"
              >
                <i
                  className={`fa-solid ${
                    callLoading ? "fa-spinner fa-spin" : "fa-phone"
                  }`}
                  aria-hidden="true"
                />
              </button>
              {callMenuOpen ? (
                <div
                  className={styles.headerMenu}
                  role="menu"
                  aria-label={`Call ${friendName}`}
                  data-testid="mini-chat-call-menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.headerMenuItem}
                    onClick={(event) => {
                      event.stopPropagation();
                      void initiateCall("audio");
                    }}
                    disabled={Boolean(callLoading)}
                    data-testid="mini-chat-call-audio"
                  >
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                    <span>Voice call</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.headerMenuItem}
                    onClick={(event) => {
                      event.stopPropagation();
                      void initiateCall("video");
                    }}
                    disabled={Boolean(callLoading)}
                    data-testid="mini-chat-call-video"
                  >
                    <i className="fa-solid fa-video" aria-hidden="true" />
                    <span>Video call</span>
                  </button>
                </div>
              ) : null}
            </span>
          ) : null}
          {!isUnifloAssistant && friendUserId ? (
            <span className={styles.bondMenuWrap} ref={bondMenuRef}>
              <button
                type="button"
                className={styles.headerBtn}
                onClick={handleBondMenuToggle}
                title={`Bond actions for ${friendName}`}
                aria-label={`Bond actions for ${friendName}`}
                aria-haspopup="menu"
                aria-expanded={bondMenuOpen}
                data-testid="mini-chat-bond-actions"
              >
                <BondIcon />
              </button>
              {bondMenuOpen ? (
                <div
                  className={styles.bondMenu}
                  role="menu"
                  aria-label={`Bond actions for ${friendName}`}
                  data-testid="mini-chat-bond-menu"
                >
                  {bondStatusLoading ? (
                    <div className={styles.bondMenuStatus}>Loading actions...</div>
                  ) : bondActions.length > 0 ? (
                    bondActions.map((action) => (
                      <button
                        key={action.id}
                        type="button"
                        role="menuitem"
                        className={`${styles.bondMenuItem} ${action.danger ? styles.bondMenuItemDanger : ""}`.trim()}
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleBondAction(action);
                        }}
                        disabled={Boolean(bondActionBusy)}
                        data-testid={`mini-chat-bond-action-${action.id}`}
                      >
                        <span>{action.label}</span>
                        {bondActionBusy === action.id ? (
                          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                        ) : null}
                      </button>
                    ))
                  ) : (
                    <div className={styles.bondMenuStatus}>
                      {bondStatus === "blocked_by_them"
                        ? "This member has blocked bonding."
                        : "No bond actions available."}
                    </div>
                  )}
                </div>
              ) : null}
            </span>
          ) : null}
          <button
            className={styles.headerBtn}
            onClick={(e) => {
              e.stopPropagation();
              toggleMinimize(chatId);
            }}
            title={minimized ? "Expand" : "Minimize"}
            aria-label={minimized ? "Expand chat" : "Minimize chat"}
            data-testid="mini-chat-toggle"
          >
            <i
              className={`fa-solid ${
                minimized ? "fa-chevron-up" : "fa-chevron-down"
              }`}
              aria-hidden="true"
            />
          </button>
          <button
            className={styles.headerBtn}
            onClick={(e) => {
              e.stopPropagation();
              closeMiniChat(chatId);
            }}
            title="Close"
            aria-label="Close chat"
            data-testid="mini-chat-close"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </span>
      </div>

      {/* ── Message area ─────────────────────────────────────────── */}
      {!minimized && (
        <>
          <div
            id={messageAreaId}
            ref={messageAreaRef}
            className={styles.messageArea}
            role="log"
            aria-live="polite"
            aria-relevant="additions text"
            data-testid="mini-chat-messages"
          >
            {/* Drop zone overlay */}
            {dragging && (
              <div className={styles.dropOverlay}>
                <i
                  className={`fa-solid ${
                    dragMode === "card" ? "fa-rectangle-list" : "fa-cloud-arrow-up"
                  }`}
                  aria-hidden="true"
                />
                <span>
                  {dragMode === "card"
                    ? "Drop card or stack here"
                    : "Drop files here"}
                </span>
              </div>
            )}

            {!loading && messages.length > 0 && messagesPage < messagesTotalPages ? (
              <div className={styles.historyControl}>
                <button
                  type="button"
                  className={styles.historyButton}
                  onClick={loadOlderMessages}
                  disabled={loadingOlderMessages}
                  data-testid="mini-chat-load-older"
                >
                  {loadingOlderMessages ? (
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                  ) : (
                    <i className="fa-solid fa-clock-rotate-left" aria-hidden="true" />
                  )}
                  <span>
                    {loadingOlderMessages ? "Loading history..." : "Load older messages"}
                  </span>
                </button>
              </div>
            ) : null}

            {loading ? (
              <div className={styles.loading}>Loading messages...</div>
            ) : error && messages.length === 0 ? (
              <div className={`${styles.loading} ${styles.loadingError}`}>
                {error}
              </div>
            ) : messages.length === 0 ? (
              <div className={styles.emptyChat}>Say hi to {friendName}!</div>
            ) : (
              messages.map((m) => {
                const senderId = String(
                  m?.sender?._id || m?.sender?.id || m?.sender,
                );
                const isMine =
                  senderId === String(currentUser?._id || currentUser?.id);
                const messageKey = m._id || `${senderId}-${m?.createdAt}`;
                const canCopyMessage = `${m?.content || ""}`.trim().length > 0;
                const canShareMessage = canExternallyShareMessage(m);
                const copied = copiedMessageKey === messageKey;
                const callMessage = isCallMessage(m);

                return (
                  <div
                    key={messageKey}
                    className={`${styles.msgRow} ${
                      isMine ? styles.msgRowSent : styles.msgRowReceived
                    }`}
                  >
                    <div
                      className={`${styles.msgBubble} ${
                        isMine ? styles.bubbleSent : styles.bubbleReceived
                      }`}
                    >
                      {/* Attachments */}
                      {Array.isArray(m?.attachments) &&
                        m.attachments.length > 0 && (
                          <div
                            className={`${styles.messageAttachments} ${
                              m?.content ? styles.messageAttachmentsWithText : ""
                            }`}
                          >
                            {m.attachments.map((att, idx) => {
                              const metadata = getFileTypeMetadata(att);
                              const attachmentType = inferAttachmentType({
                                ...att,
                                ...metadata,
                              });
                              return attachmentType === "image" ? (
                                <a
                                  key={idx}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.attachmentLink}
                                >
                                  <img
                                    src={att.url}
                                    alt={att.name || ""}
                                    onLoad={handleAttachmentLayout}
                                    className={styles.attachmentMedia}
                                  />
                                </a>
                              ) : attachmentType === "video" ? (
                                <video
                                  key={idx}
                                  src={att.url}
                                  controls
                                  onLoadedMetadata={handleAttachmentLayout}
                                  className={styles.attachmentMedia}
                                />
                              ) : attachmentType === "audio" ||
                                att.type === "voice_note" ? (
                                <div
                                  key={idx}
                                  className={styles.attachmentVoiceNote}
                                >
                                  <VoiceNotePlayer
                                    src={att.url}
                                    durationMs={att.durationMs}
                                    isMine={isMine}
                                    compact
                                    label={
                                      att.type === "voice_note"
                                        ? "Voice note"
                                        : att.name || "Audio"
                                    }
                                  />
                                </div>
                              ) : (
                                <a
                                  key={idx}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.attachmentFileLink}
                                >
                                  <i
                                    className={metadata.iconClass || "fa-solid fa-file"}
                                    aria-hidden="true"
                                  />
                                  <span>{att.name || "Download"}</span>
                                  {metadata.appName ? (
                                    <span className={styles.attachmentFileType}>
                                      {metadata.appName}
                                    </span>
                                  ) : null}
                                </a>
                              );
                            })}
                          </div>
                        )}

                      {Array.isArray(m?.cardReferences) &&
                        m.cardReferences.length > 0 ? (
                          <div
                            className={`${styles.messageCards} ${
                              m?.content ? styles.messageCardsWithText : ""
                            }`}
                          >
                            {m.cardReferences.map((cardRef) => {
                              const cardId = `${cardRef?.cardId || ""}`.trim();
                              const cardType =
                                cardRef?.type === "stack" ? "stack" : "card";
                              const title =
                                `${cardRef?.title || ""}`.trim() ||
                                (cardType === "stack"
                                  ? "Untitled stack"
                                  : "Untitled card");
                              return (
                                <button
                                  key={cardId || title}
                                  type="button"
                                  className={`${styles.messageCardRef} ${
                                    isMine
                                      ? styles.messageCardRefSent
                                      : styles.messageCardRefReceived
                                  }`}
                                  onClick={() => handleOpenCardReference(cardRef)}
                                  data-testid="mini-chat-card-reference"
                                  aria-label={`Open ${cardType} ${title}`}
                                >
                                  <span className={styles.messageCardRefIcon}>
                                    <i
                                      className={`fa-solid ${
                                        cardType === "stack"
                                          ? "fa-layer-group"
                                          : "fa-rectangle-list"
                                      }`}
                                      aria-hidden="true"
                                    />
                                  </span>
                                  <span className={styles.messageCardRefBody}>
                                    <span className={styles.messageCardRefTitle}>
                                      {title}
                                    </span>
                                    <span className={styles.messageCardRefMeta}>
                                      {formatCardReferenceMeta(cardRef)}
                                    </span>
                                    {cardRef?.description ? (
                                      <span
                                        className={styles.messageCardRefDescription}
                                      >
                                        {cardRef.description}
                                      </span>
                                    ) : null}
                                  </span>
                                  <i
                                    className="fa-solid fa-arrow-up-right-from-square"
                                    aria-hidden="true"
                                  />
                                </button>
                              );
                            })}
                          </div>
                        ) : null}

                      {canShareMessage && !m?.content ? (
                        <div
                          className={`${styles.messageActionRow} ${
                            isMine
                              ? styles.messageActionRowSent
                              : styles.messageActionRowReceived
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => shareMessageToExternalApp(m)}
                            title="Share externally"
                            aria-label="Share externally"
                            className={`${styles.messagePillAction} ${
                              isMine ? styles.actionOnSent : styles.actionOnReceived
                            }`}
                            data-testid="mini-chat-share-attachment"
                          >
                            <i
                              className="fa-solid fa-share-nodes"
                              aria-hidden="true"
                            />
                            <span>Share</span>
                          </button>
                        </div>
                      ) : null}

                      {/* Text */}
                      {m?.content && (
                        <div className={styles.messageContentRow}>
                          <span
                            className={`${styles.messageText} ${
                              callMessage ? styles.callMessageText : ""
                            }`}
                          >
                            {callMessage ? (
                              <i
                                className={`fa-solid ${getCallIconClass(m?.call)}`}
                                aria-hidden="true"
                              />
                            ) : null}
                            <span>
                              {renderInlineRichText(
                                m.content,
                                `mini-chat-message-${messageKey}`,
                              )}
                            </span>
                          </span>
                          {canCopyMessage || canShareMessage ? (
                            <div className={styles.messageInlineActions}>
                              {canShareMessage ? (
                                <button
                                  type="button"
                                  onClick={() => shareMessageToExternalApp(m)}
                                  title="Share externally"
                                  aria-label="Share externally"
                                  className={`${styles.messageIconAction} ${
                                    isMine ? styles.actionOnSent : styles.actionOnReceived
                                  }`}
                                  data-testid="mini-chat-share-message"
                                >
                                  <i
                                    className="fa-solid fa-share-nodes"
                                    aria-hidden="true"
                                  />
                                </button>
                              ) : null}
                              {canCopyMessage ? (
                                <CopyToClipboard
                                  text={m.content}
                                  onCopy={() => handleMessageCopied(messageKey)}
                                >
                                  <button
                                    type="button"
                                    title={copied ? "Copied" : "Copy message"}
                                    aria-label={
                                      copied ? "Message copied" : "Copy message"
                                    }
                                    className={`${styles.messageIconAction} ${
                                      isMine ? styles.actionOnSent : styles.actionOnReceived
                                    } ${copied ? styles.messageIconActionCopied : ""}`}
                                    data-testid="mini-chat-copy-message"
                                  >
                                    <i
                                      className={`fa-solid ${copied ? "fa-check" : "fa-copy"}`}
                                      aria-hidden="true"
                                    />
                                  </button>
                                </CopyToClipboard>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      )}

                      {/* Meta: time + ticks */}
                      {m?.createdAt && (
                        <div
                          className={`${styles.msgMeta} ${
                            isMine ? styles.msgMetaSent : styles.msgMetaReceived
                          }`}
                        >
                          <span>{friendlyTimeShort(m.createdAt)}</span>
                          {isMine && (
                            <span
                              className={`${styles.statusTicks} ${
                                m.status === "read" ? styles.statusTicksRead : ""
                              } ${
                                m.status === "sent" ? styles.statusTicksSent : ""
                              }`}
                              title={
                                m.status === "read"
                                  ? "Read"
                                  : m.status === "delivered"
                                    ? "Delivered"
                                    : "Sent"
                              }
                            >
                              {m.status === "sent" ? "\u2713" : "\u2713\u2713"}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          {isVoiceNoteRecording ? (
            <div className={styles.recorderStrip}>
              <div className={styles.recorderMeta}>
                <span className={styles.recordingDot} aria-hidden="true" />
                <strong className={styles.recordingLabel}>Recording</strong>
                <span className={styles.recordingTimer}>
                  {formatVoiceTimer(voiceNoteElapsedMs)}
                </span>
              </div>
              <div className={styles.recorderActions}>
                <button
                  type="button"
                  className={styles.recorderActionBtn}
                  onClick={cancelVoiceNoteRecording}
                  title="Cancel recording"
                  aria-label="Cancel recording"
                  data-testid="mini-chat-cancel-recording"
                >
                  <span>Cancel</span>
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={`${styles.recorderActionBtn} ${styles.recorderActionBtnDanger}`}
                  onClick={stopVoiceNoteRecording}
                  title="Stop recording"
                  aria-label="Stop recording"
                  data-testid="mini-chat-stop-recording"
                >
                  <span>Stop</span>
                  <i className="fa-solid fa-stop" aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : null}

          {hasPendingVoiceNote && pendingVoiceNoteUrl ? (
            <div className={styles.recorderStrip}>
              <div className={styles.voicePreview}>
                <VoiceNotePlayer
                  src={pendingVoiceNoteUrl}
                  durationMs={pendingVoiceNoteDurationMs}
                  compact
                  label="Voice note preview"
                />
              </div>
              <button
                type="button"
                className={styles.recorderActionBtn}
                onClick={clearVoiceNotePreview}
                title="Discard voice note"
                aria-label="Discard voice note"
                data-testid="mini-chat-discard-voice-note"
              >
                <span>Discard</span>
                <i className="fa-solid fa-trash" aria-hidden="true" />
              </button>
            </div>
          ) : null}

          {/* ── File previews ──────────────────────────────────────── */}
          {filePreviews.length > 0 && (
            <div className={styles.filePreviews}>
              {filePreviews.map((fp, i) => (
                <div key={i} className={styles.filePreviewItem}>
                  {fp.url ? (
                    <img
                      src={fp.url}
                      alt=""
                      className={styles.filePreviewImg}
                    />
                  ) : (
                    <div className={styles.filePreviewGeneric}>
                      <i
                        className={fp.metadata?.iconClass || "fa-solid fa-file"}
                        aria-hidden="true"
                      />
                    </div>
                  )}
                  <button
                    className={styles.filePreviewRemove}
                    onClick={() => removeFile(i)}
                    title="Remove"
                    aria-label={`Remove ${fp.name || "attachment"}`}
                    data-testid="mini-chat-remove-file"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Upload progress ─────────────────────────────────────── */}
          {sending && selectedFiles.length > 0 && (
            <div className={styles.uploadBar}>
              <div
                className={styles.uploadBarFill}
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {/* ── Input area ───────────────────────────────────────── */}
          <form className={styles.inputArea} onSubmit={handleSend}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ANY_FILE_ACCEPT}
              onChange={handleFileSelect}
              className={styles.fileInput}
              aria-label="Attach files"
              data-testid="mini-chat-file-input"
            />
            <button
              type="button"
              className={styles.attachBtn}
              onClick={() => fileInputRef.current?.click()}
              title="Attach file"
              aria-label="Attach file"
              data-testid="mini-chat-attach-file"
              disabled={
                !resolvedConversationId ||
                loading ||
                isVoiceNoteRecording ||
                isVoiceNoteProcessing
              }
            >
              <i className="fa-solid fa-paperclip" aria-hidden="true" />
            </button>
            <textarea
              ref={inputRef}
              className={styles.inputField}
              placeholder={
                isVoiceNoteRecording
                  ? "Recording voice note…"
                  : hasPendingVoiceNote
                    ? "Send your voice note or discard it"
                    : "Type a message"
              }
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              disabled={
                !resolvedConversationId ||
                loading ||
                isVoiceNoteRecording ||
                isVoiceNoteProcessing ||
                hasPendingVoiceNote
              }
              rows={1}
              aria-label={`Message ${friendName}`}
              data-testid="mini-chat-message-input"
            />
            {voiceNotesSupported &&
            !draft.trim() &&
            selectedFiles.length === 0 &&
            !hasPendingVoiceNote &&
            !isVoiceNoteRecording ? (
              <button
                type="button"
                className={styles.attachBtn}
                onClick={startVoiceNoteRecording}
                title="Record voice note"
                aria-label="Record voice note"
                data-testid="mini-chat-record-voice-note"
                disabled={!resolvedConversationId || loading || isVoiceNoteProcessing}
              >
                <i className="fa-solid fa-microphone" aria-hidden="true" />
              </button>
            ) : null}
            <button
              type="submit"
              className={styles.sendBtn}
              disabled={
                (!draft.trim() &&
                  selectedFiles.length === 0 &&
                  !hasPendingVoiceNote) ||
                !resolvedConversationId ||
                sending ||
                isVoiceNoteRecording ||
                isVoiceNoteProcessing
              }
              title="Send"
              aria-label="Send message"
              data-testid="mini-chat-send-message"
            >
              <i className="fa-solid fa-paper-plane" aria-hidden="true" />
            </button>
          </form>

          {/* ── Error toast ──────────────────────────────────────── */}
          {error && messages.length > 0 && (
            <div className={styles.errorToast} role="alert">
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
}
